import { getFormattedRootFields } from 'app/services/formula/parser/syntaxParser/parserUtils';
import _ from 'lodash';

import {
  ARRAY_FUNCTIONS_WITH_ITERATORS,
  FIELD_KEYS,
  LEX_TOKEN_TYPES,
  NUMERIC_TYPES,
  STRING_TYPES,
} from 'constants/parser';

import {
  DEFAULT_OPERATORS_BY_CATEGORY,
  OPERATOR_GLOSSES,
  SUGGESTION_CONTEXTS,
} from './suggestionEngine.constants';

// Private helpers

/**
 * Split a camelCase/PascalCase segment into spaced Title Case words.
 * Acronym runs (2+ consecutive uppercase letters) are kept together.
 * Underscore-prefixed names (iterator variables) are returned unchanged.
 * Examples:
 *   'CreditScore'        → 'Credit Score'
 *   'APRRate'            → 'APR Rate'
 *   'IsSignatureRequired'→ 'Is Signature Required'
 *   '_borrower'          → '_borrower'
 *
 * @param {string} segment
 * @returns {string}
 */
export const prettifyName = segment => {
  if (!segment || segment.startsWith('_')) {
    return segment;
  }

  // Insert a space before each uppercase letter that follows a lowercase letter,
  // or before an uppercase letter that is followed by a lowercase letter when the
  // preceding character was also uppercase (acronym boundary: 'APRRate' → 'APR Rate').
  const spaced = segment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

  // Title-case the first word so 'creditScore' → 'Credit Score'.
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Resolve a type string through entities indirection with a cycle guard.
 * Returns the entity object (plain object), or the primitive/enum type string.
 *
 * @param {string} typeStr
 * @param {Object} entities
 * @param {Set} visited
 * @returns {Object|string|null}
 */
const resolveType = (typeStr, entities, visited) => {
  if (_.isNil(typeStr)) {
    return null;
  }

  if (visited.has(typeStr)) {
    return null;
  }

  const entity = entities[typeStr];

  // The schema ships empty placeholders for primitive types (entities.string
  // === {}). Treat those as the primitive itself, not a navigable entity.
  if (_.isPlainObject(entity)) {
    return _.isEmpty(entity) ? typeStr : entity;
  }

  if (_.isString(entity)) {
    const next = new Set(visited);

    next.add(typeStr);

    return resolveType(entity, entities, next);
  }

  return typeStr;
};

/**
 * Walk remaining segments through an entity object.
 *
 * @param {string[]} segments
 * @param {Object} entityObj
 * @param {Object} entities
 * @param {Set} visited
 * @returns {Object|string|null}
 */
const walkPathFromEntity = (segments, entityObj, entities, visited) => {
  if (!segments.length) {
    return entityObj;
  }

  const [head, ...tail] = segments;
  const typeStr = entityObj[head];

  if (_.isNil(typeStr)) {
    return null;
  }

  const resolved = resolveType(typeStr, entities, visited);

  if (!tail.length) {
    return resolved;
  }

  if (!_.isPlainObject(resolved)) {
    return null;
  }

  return walkPathFromEntity(tail, resolved, entities, new Set([...visited, head]));
};

/**
 * Retry walk by treating the first segment as any known entity key directly.
 *
 * @param {string[]} segments
 * @param {Object} schemaParams
 * @returns {Object|string|null}
 */
const tryFromRootEntries = (segments, schemaParams) => {
  const { entities } = schemaParams;
  const [first, ...rest] = segments;
  const entity = entities[first];

  if (!_.isPlainObject(entity)) {
    return null;
  }

  if (!rest.length) {
    return entity;
  }

  return walkPathFromEntity(rest, entity, entities, new Set([first]));
};

/**
 * Walk dot-separated path segments through entities.
 * Returns the resolved type (object or string) of the final segment, or null.
 *
 * @param {string[]} segments
 * @param {Object} schemaParams
 * @returns {Object|string|null}
 */
const walkPath = (segments, schemaParams) => {
  const { entities } = schemaParams;
  const [first, ...rest] = segments;
  const rootEntity = entities[first];

  if (!_.isPlainObject(rootEntity)) {
    return tryFromRootEntries(segments, schemaParams);
  }

  if (!rest.length) {
    return rootEntity;
  }

  return (
    walkPathFromEntity(rest, rootEntity, entities, new Set([first])) ??
    tryFromRootEntries(segments, schemaParams)
  );
};

/**
 * Resolve a field path string like 'loan.borrower' to its entity/type.
 *
 * @param {string} fieldPath
 * @param {Object} schemaParams
 * @returns {Object|string|null}
 */
const resolveFieldPath = (fieldPath, schemaParams) => {
  const segments = fieldPath.split('.');

  return walkPath(segments, schemaParams);
};

/**
 * Determine the default operator category for a primitive type string.
 *
 * @param {string} typeStr
 * @param {Object} enumTypes
 * @returns {string}
 */
const getTypeCategory = (typeStr, enumTypes) => {
  if (NUMERIC_TYPES.includes(typeStr)) {
    return 'numeric';
  }

  if (STRING_TYPES.includes(typeStr)) {
    return 'string';
  }

  if (typeStr === LEX_TOKEN_TYPES.bool || typeStr === 'bool') {
    return 'bool';
  }

  if (typeStr === LEX_TOKEN_TYPES.date || typeStr === 'date') {
    return 'date';
  }

  if (!_.isNil(enumTypes) && !_.isNil(enumTypes[typeStr])) {
    return 'enum';
  }

  return 'string';
};

/**
 * Build a sortKey string from a numeric bucket and a label.
 *
 * @param {number} bucket
 * @param {string} label
 * @returns {string}
 */
const makeSortKey = (bucket, label) => `${bucket}_${label}`;

/**
 * Count single-quote characters in a string.
 *
 * @param {string} text
 * @returns {number}
 */
const countSingleQuotes = text => (text.match(/'/g) || []).length;

// Accumulator shape for the backward-scan reduce over scanForUnclosedCall.
const SCAN_INIT = { depth: 0, inString: false, commas: 0, parenPos: -1, done: false };

/**
 * Process a single character during the right-to-left backward scan.
 * Returns the updated accumulator state.
 *
 * @param {Object} acc
 * @param {string} ch
 * @param {number} pos
 * @returns {Object}
 */
const scanStep = (acc, ch, pos) => {
  if (acc.done) {
    return acc;
  }

  if (ch === "'") {
    return { ...acc, inString: !acc.inString };
  }

  if (acc.inString) {
    return acc;
  }

  if (ch === ')') {
    return { ...acc, depth: acc.depth + 1 };
  }

  if (ch === '(') {
    if (acc.depth === 0) {
      return { ...acc, parenPos: pos, done: true };
    }

    return { ...acc, depth: acc.depth - 1 };
  }

  if (ch === ',' && acc.depth === 0) {
    return { ...acc, commas: acc.commas + 1 };
  }

  return acc;
};

/**
 * Accumulator shape for the forward-scan reduce that extracts the first argument text.
 */
const FIRST_ARG_SCAN_INIT = { depth: 0, inString: false, done: false, chars: [] };

/**
 * Process a single character during the forward scan to extract the first argument text.
 * Accumulates characters until a top-level comma (depth 0, outside strings) or end of input.
 *
 * @param {Object} acc
 * @param {string} ch
 * @returns {Object}
 */
const firstArgScanStep = (acc, ch) => {
  if (acc.done) {
    return acc;
  }

  if (ch === "'") {
    return { ...acc, inString: !acc.inString, chars: [...acc.chars, ch] };
  }

  if (acc.inString) {
    return { ...acc, chars: [...acc.chars, ch] };
  }

  if (ch === '(') {
    return { ...acc, depth: acc.depth + 1, chars: [...acc.chars, ch] };
  }

  if (ch === ')') {
    if (acc.depth === 0) {
      return { ...acc, done: true };
    }

    return { ...acc, depth: acc.depth - 1, chars: [...acc.chars, ch] };
  }

  if (ch === ',' && acc.depth === 0) {
    return { ...acc, done: true };
  }

  return { ...acc, chars: [...acc.chars, ch] };
};

const ITERATOR_NAME_RE = /^_[A-Za-z]\w*$/;

/**
 * Split the text after an unclosed '(' into its top-level comma-separated
 * arguments (ignoring commas inside nested parens or strings). The final entry
 * may be an in-progress argument the caller is still typing.
 *
 * @param {string} afterParen
 * @returns {string[]}
 */
const splitTopLevelArgs = afterParen => {
  const result = afterParen.split('').reduce(
    (acc, ch) => {
      if (acc.closed) {
        return acc;
      }

      if (ch === "'") {
        return { ...acc, inString: !acc.inString, current: acc.current + ch };
      }

      if (acc.inString) {
        return { ...acc, current: acc.current + ch };
      }

      if (ch === '(') {
        return { ...acc, depth: acc.depth + 1, current: acc.current + ch };
      }

      if (ch === ')' && acc.depth === 0) {
        return { ...acc, closed: true, args: [...acc.args, acc.current] };
      }

      if (ch === ')') {
        return { ...acc, depth: acc.depth - 1, current: acc.current + ch };
      }

      if (ch === ',' && acc.depth === 0) {
        return { ...acc, args: [...acc.args, acc.current], current: '' };
      }

      return { ...acc, current: acc.current + ch };
    },
    { depth: 0, inString: false, closed: false, args: [], current: '' },
  );

  return result.closed ? result.args : [...result.args, result.current];
};

/**
 * Backward-scan textBeforeCursor to find the innermost unclosed '('.
 * Returns { funcName, activeParameter, firstArgText, declaredIterator } or null.
 * declaredIterator is the user-typed iterator name (2nd arg) when it is an
 * underscore-prefixed identifier, else null.
 *
 * @param {string} text
 * @returns {{ funcName: string, activeParameter: number, firstArgText: string, declaredIterator: string|null }|null}
 */
const scanForUnclosedCall = text => {
  const chars = text.split('').reverse();
  const finalState = chars.reduce(
    (acc, ch, idx) => scanStep(acc, ch, text.length - 1 - idx),
    SCAN_INIT,
  );

  if (finalState.parenPos < 0) {
    return null;
  }

  const before = text.slice(0, finalState.parenPos);
  const identMatch = before.match(/([A-Za-z_][A-Za-z0-9_]*)[\s]*$/);

  if (!identMatch) {
    return null;
  }

  const afterParen = text.slice(finalState.parenPos + 1);
  const firstArgState = afterParen.split('').reduce(firstArgScanStep, FIRST_ARG_SCAN_INIT);
  const firstArgText = firstArgState.chars.join('').trim();

  const secondArg = (splitTopLevelArgs(afterParen)[1] ?? '').trim();
  const declaredIterator = ITERATOR_NAME_RE.test(secondArg) ? secondArg : null;

  return {
    funcName: identMatch[1],
    activeParameter: finalState.commas,
    firstArgText,
    declaredIterator,
  };
};

/**
 * True when a path's first segment names the iterator bound by the enclosing
 * array function — either the auto-derived name or the user-declared one.
 *
 * @param {string} firstSegment
 * @param {{ firstArgText: string, declaredIterator: string|null }} callInfo
 * @returns {boolean}
 */
const matchesEnclosingIterator = (firstSegment, callInfo) => {
  const derived = deriveIteratorName((callInfo.firstArgText ?? '').trim());

  return firstSegment === derived || firstSegment === callInfo.declaredIterator;
};

const OPEN_CALLS_INIT = { depth: 0, inString: false, identStart: -1, calls: [] };

/**
 * Forward-scan text and return every function call still open at the cursor,
 * outermost first, each with its first argument and declared iterator. Unlike
 * scanForUnclosedCall (innermost only), this exposes the whole enclosing stack
 * so an iterator bound by an OUTER collection function is still found when the
 * cursor sits inside a nested group/function.
 *
 * @param {string} text
 * @returns {{ funcName: string, firstArgText: string, declaredIterator: string|null }[]}
 */
const collectOpenCalls = text => {
  const finalState = text.split('').reduce((acc, ch, index) => {
    if (ch === "'") {
      return { ...acc, inString: !acc.inString, identStart: -1 };
    }

    if (acc.inString) {
      return acc;
    }

    if (/[A-Za-z0-9_.]/.test(ch)) {
      return { ...acc, identStart: acc.identStart < 0 ? index : acc.identStart };
    }

    if (ch === '(') {
      const name = acc.identStart >= 0 ? text.slice(acc.identStart, index) : '';

      return {
        ...acc,
        depth: acc.depth + 1,
        identStart: -1,
        calls: [...acc.calls, { name, depth: acc.depth, argStart: index + 1 }],
      };
    }

    if (ch === ')') {
      const newDepth = acc.depth - 1;
      const last = acc.calls[acc.calls.length - 1];
      const calls = last && last.depth === newDepth ? acc.calls.slice(0, -1) : acc.calls;

      return { ...acc, depth: newDepth, identStart: -1, calls };
    }

    return { ...acc, identStart: -1 };
  }, OPEN_CALLS_INIT);

  return finalState.calls.map(call => {
    const args = splitTopLevelArgs(text.slice(call.argStart));
    const firstArgText = (args[0] ?? '').trim();
    const secondArg = (args[1] ?? '').trim();

    return {
      funcName: call.name,
      firstArgText,
      declaredIterator: ITERATOR_NAME_RE.test(secondArg) ? secondArg : null,
    };
  });
};

/**
 * Find the innermost enclosing call whose bound iterator (derived or declared)
 * matches firstSegment, searching the full open-call stack.
 *
 * @param {string} text
 * @param {string} firstSegment
 * @returns {{ funcName: string, firstArgText: string, declaredIterator: string|null }|null}
 */
const findIteratorBindingCall = (text, firstSegment) =>
  [...collectOpenCalls(text)]
    .reverse()
    .find(call => matchesEnclosingIterator(firstSegment, call)) ?? null;

/**
 * Build child-field suggestions for a resolved entity object.
 *
 * @param {Object} entityObj
 * @param {Object} schemaParams
 * @param {boolean} isOnlyBooleanAllowed
 * @returns {Array}
 */
const buildChildSuggestions = (entityObj, schemaParams, isOnlyBooleanAllowed) => {
  const { entities, enumTypes } = schemaParams;

  return Object.entries(entityObj).map(([childKey, typeStr]) => {
    const resolvedChild = resolveType(typeStr, entities, new Set());
    const isEntity = _.isPlainObject(resolvedChild);
    const isBoolReturn = typeStr === 'bool' || typeStr === LEX_TOKEN_TYPES.bool;
    const bucket = isOnlyBooleanAllowed && isBoolReturn ? 0 : 1;

    const getTypeSuffix = () => {
      if (isEntity) {
        return 'entity';
      }

      if (!_.isNil(enumTypes) && !_.isNil(enumTypes[typeStr])) {
        return 'enum';
      }

      if (_.isString(typeStr) && typeStr.startsWith(LEX_TOKEN_TYPES.array)) {
        return 'array';
      }

      return typeStr;
    };

    // An entity child is drillable: insert the trailing dot and flag it so the
    // popover shows the entity chip and the editor re-triggers child suggestions.
    return {
      key: 'Fields',
      value: childKey,
      label: prettifyName(childKey),
      name: `${childKey} · ${getTypeSuffix()}`,
      insertText: isEntity ? `${childKey}.` : childKey,
      isEntity,
      sortKey: makeSortKey(bucket, childKey),
    };
  });
};

const COMPARISON_OPS = new Set(['==', '!=', '>=', '<=', '>', '<']);

/**
 * Build operator suggestions for a given primitive/enum type string.
 *
 * @param {string} typeStr
 * @param {Object} schemaParams
 * @param {boolean} isOnlyBooleanAllowed
 * @returns {Array}
 */
const buildOperatorSuggestions = (typeStr, schemaParams, isOnlyBooleanAllowed) => {
  const { commonTypes, enumTypes } = schemaParams;
  const ops =
    commonTypes?.[typeStr] ??
    DEFAULT_OPERATORS_BY_CATEGORY[getTypeCategory(typeStr, enumTypes)] ??
    [];

  return ops.map(op => {
    const gloss = OPERATOR_GLOSSES[op] ?? op;
    const isComparison = COMPARISON_OPS.has(op);
    const bucket = isOnlyBooleanAllowed && isComparison ? 0 : 1;

    return {
      key: 'Operators',
      value: op,
      name: gloss,
      insertText: `${op} `,
      sortKey: makeSortKey(bucket, op),
    };
  });
};

/**
 * Build root-level suggestions (fields + functions).
 *
 * @param {Object} schemaParams
 * @param {boolean} isOnlyBooleanAllowed
 * @returns {Array}
 */
const buildRootSuggestions = (schemaParams, isOnlyBooleanAllowed) => {
  const rootFields = getFormattedRootFields(schemaParams).map(f => {
    const isBoolType = f.name.includes('(bool)');
    const isEntity = f.name.includes('(entity)');
    const bucket = isOnlyBooleanAllowed && isBoolType ? 0 : 1;
    const lastSegment = f.value.split('.').pop();

    return {
      ...f,
      label: prettifyName(lastSegment),
      name: f.name.replace(/\(([^)]+)\)/, '· $1'),
      insertText: isEntity ? `${f.value}.` : f.value,
      isEntity,
      sortKey: makeSortKey(bucket, f.value),
    };
  });

  const functionSuggestions = Object.keys(schemaParams?.functions ?? {}).map(name => {
    const fn = schemaParams.functions[name];
    const returnType = fn?.returnType ?? '';
    const isBoolReturn = returnType === 'bool';
    const bucket = isOnlyBooleanAllowed && isBoolReturn ? 0 : 2;

    // Insert the opening paren so the caret lands inside the call and signature
    // help fires immediately, instead of the user typing "(" by hand.
    return {
      key: 'Functions',
      value: name,
      name: `${name} → ${returnType}`,
      insertText: `${name}(`,
      sortKey: makeSortKey(bucket, name),
    };
  });

  return [...rootFields, ...functionSuggestions];
};

// Iterator helpers

const VALID_PATH_RE = /^[\w.]+$/;

/**
 * Derive the auto-generated iterator name from an array field path.
 * Rule: lowercase the last '.'-separated segment, strip one trailing 's' if present, prefix '_'.
 * Returns null for empty or non-path input.
 *
 * @param {string} arrayPath
 * @returns {string|null}
 */
export const deriveIteratorName = arrayPath => {
  if (!arrayPath || !VALID_PATH_RE.test(arrayPath)) {
    return null;
  }

  const lastSegment = arrayPath.toLowerCase().split('.').pop();

  if (!lastSegment) {
    return null;
  }

  const stripped = lastSegment.endsWith('s') ? lastSegment.slice(0, -1) : lastSegment;

  return `_${stripped}`;
};

/**
 * Resolve the element entity object for an array field path via entities indirection.
 * Returns null if the path doesn't resolve to an array type or the element entity is unknown.
 *
 * @param {string} arrayFieldPath
 * @param {Object} schemaParams
 * @returns {Object|null}
 */
const ARRAY_ELEMENT_RE = /^array[<[]([\w]+)[>\]]$/;

const resolveArrayElementEntity = (arrayFieldPath, schemaParams) => {
  const arrayTypeStr = resolveFieldPath(arrayFieldPath, schemaParams);

  if (!_.isString(arrayTypeStr)) {
    return null;
  }

  const match = ARRAY_ELEMENT_RE.exec(arrayTypeStr);

  if (!match) {
    return null;
  }

  return resolveType(match[1], schemaParams.entities, new Set());
};

/**
 * Attempt iterator drill-down: when pathBeforeDot starts with a derived iterator name,
 * resolve the array element entity and walk any remaining segments through it.
 * Returns a PATH_DRILL_DOWN result or null.
 *
 * @param {{ text: string, pathBeforeDot: string, partialLen: number, schemaParams: Object, isOnlyBooleanAllowed: boolean }} params
 * @returns {{ context, replaceLength, suggestions }|null}
 */
const tryIteratorDrillDown = ({
  text,
  pathBeforeDot,
  partialLen,
  schemaParams,
  isOnlyBooleanAllowed,
}) => {
  const pathSegments = pathBeforeDot.split('.');
  const firstSegment = pathSegments[0];
  const callInfo = findIteratorBindingCall(text, firstSegment);

  if (_.isNil(callInfo)) {
    return null;
  }

  const firstArgText = (callInfo.firstArgText ?? '').trim();
  const elementEntity = resolveArrayElementEntity(firstArgText, schemaParams);

  if (!_.isPlainObject(elementEntity)) {
    return null;
  }

  const remainingSegments = pathSegments.slice(1);
  const finalEntity = remainingSegments.length
    ? walkPathFromEntity(remainingSegments, elementEntity, schemaParams.entities, new Set())
    : elementEntity;

  if (!_.isPlainObject(finalEntity)) {
    return null;
  }

  return {
    context: SUGGESTION_CONTEXTS.PATH_DRILL_DOWN,
    replaceLength: partialLen,
    suggestions: buildChildSuggestions(finalEntity, schemaParams, isOnlyBooleanAllowed),
  };
};

/**
 * Attempt ITERATOR context detection when the cursor is at the iterator-name argument
 * of an array function (e.g. ANY(loan.applicants, <cursor>)).
 *
 * @param {string} text
 * @param {string} partial
 * @param {Object} schemaParams
 * @returns {{ context, replaceLength, suggestions }|null}
 */
const tryIteratorContext = (text, partial, schemaParams) => {
  const callInfo = scanForUnclosedCall(text);

  if (_.isNil(callInfo)) {
    return null;
  }

  const fn = schemaParams?.functions?.[callInfo.funcName];

  if (_.isNil(fn)) {
    return null;
  }

  const argKey = String(callInfo.activeParameter + 1);
  const argType = fn.arguments?.[argKey]?.type;
  const isIteratorSlot =
    argType === 'iterator' ||
    (ARRAY_FUNCTIONS_WITH_ITERATORS.includes(callInfo.funcName) && callInfo.activeParameter === 1);

  if (!isIteratorSlot) {
    return null;
  }

  const firstArgText = callInfo.firstArgText ?? '';

  if (!VALID_PATH_RE.test(firstArgText.trim())) {
    return null;
  }

  const name = deriveIteratorName(firstArgText.trim());

  if (_.isNil(name)) {
    return null;
  }

  // Guard: only fire when partial is empty or a case-insensitive prefix of the derived name.
  if (partial && !name.toLowerCase().startsWith(partial.toLowerCase())) {
    return null;
  }

  const lastSegment = firstArgText.trim().toLowerCase().split('.').pop();

  return {
    context: SUGGESTION_CONTEXTS.ITERATOR,
    replaceLength: partial.length,
    suggestions: [
      {
        key: 'Iterators',
        value: name,
        name: `element of ${lastSegment} — start the predicate`,
        insertText: name,
        sortKey: `0_${name}`,
      },
    ],
  };
};

// Tail tokenization helpers

const NAMED_OP_SUFFIX_RE = /(?:contains|startsWith|endsWith)$/;
const SYM_OP_SUFFIX_RE = /(>=|<=|==|!=|[><+\-*/])[\s]*$/;
const FIELD_SUFFIX_RE = /([\w.]+)[\s]*$/;
const OPERATOR_CHARS_RE = /[><=!+\-*/]/;

/**
 * Strip trailing partial word token.
 * Returns { partial, base } where base is text without the partial.
 *
 * @param {string} text
 * @returns {{ partial: string, base: string }}
 */
const stripTrailingPartial = text => {
  const wordPartial = text.match(/([\w]*)$/)?.[1] ?? '';

  return { partial: wordPartial, base: text.slice(0, text.length - wordPartial.length) };
};

/**
 * Classify the trailing meaningful token in `base` (after partial stripped).
 * Returns a descriptor string: 'empty' | 'openParen' | 'comma' |
 * 'operator:<op>' | 'field:<path>' | 'unknown'.
 *
 * @param {string} base
 * @returns {string}
 */
const classifyTrailing = base => {
  const trimmed = base.trimEnd();

  if (!trimmed) {
    return 'empty';
  }

  const lastChar = trimmed[trimmed.length - 1];

  if (lastChar === '(') {
    return 'openParen';
  }

  if (lastChar === ',') {
    return 'comma';
  }

  const namedOpMatch = trimmed.match(NAMED_OP_SUFFIX_RE);

  if (namedOpMatch) {
    return `operator:${namedOpMatch[0]}`;
  }

  const symOpMatch = trimmed.match(SYM_OP_SUFFIX_RE);

  if (symOpMatch) {
    return `operator:${symOpMatch[1]}`;
  }

  const fieldMatch = trimmed.match(FIELD_SUFFIX_RE);

  if (fieldMatch) {
    return `field:${fieldMatch[1]}`;
  }

  return 'unknown';
};

// VALUE context helpers

const FULL_OP_SUFFIX_RE = /(>=|<=|==|!=|[><]|contains|startsWith|endsWith)[\s]*$/;
const PAD_WIDTH = 4;

/**
 * Build enum value suggestions, handling open-quote parity.
 *
 * @param {string[]} enumValues
 * @param {string} fullText
 * @returns {Array}
 */
const ENUM_SIBLING_PREVIEW_LIMIT = 5;

const buildEnumSiblingPreview = enumValues => {
  const head = enumValues.slice(0, ENUM_SIBLING_PREVIEW_LIMIT);
  const suffix = enumValues.length > ENUM_SIBLING_PREVIEW_LIMIT ? ' · …' : '';

  return `${head.join(' · ')}${suffix}`;
};

const buildEnumValueSuggestions = (enumValues, fullText) => {
  const isInsideQuote = countSingleQuotes(fullText) % 2 !== 0;
  const siblingPreview = buildEnumSiblingPreview(enumValues);

  return enumValues.map((val, idx) => ({
    key: FIELD_KEYS.EnumTypes,
    value: val,
    name: prettifyName(val),
    insertText: isInsideQuote ? `${val}'` : `'${val}'`,
    siblingPreview,
    sortKey: makeSortKey(0, `${String(idx).padStart(PAD_WIDTH, '0')}_${val}`),
  }));
};

/**
 * Build bool value suggestions (unquoted).
 *
 * @returns {Array}
 */
const buildBoolValueSuggestions = () =>
  ['true', 'false'].map((val, idx) => ({
    key: 'Values',
    value: val,
    name: val,
    insertText: val,
    sortKey: makeSortKey(0, `${idx}_${val}`),
  }));

/**
 * Build timezone lookup suggestions, handling open-quote parity.
 *
 * @param {string[]} values
 * @param {string} fullText
 * @returns {Array}
 */
const buildLookupSuggestions = (values, fullText) => {
  const isInsideQuote = countSingleQuotes(fullText) % 2 !== 0;

  return values.map((val, idx) => ({
    key: FIELD_KEYS.Timezones,
    value: val,
    name: val,
    insertText: isInsideQuote ? `${val}'` : `'${val}'`,
    sortKey: makeSortKey(0, `${String(idx).padStart(PAD_WIDTH, '0')}_${val}`),
  }));
};

// Context detection helpers

/**
 * Resolve a field path, falling back to iterator-context resolution when the
 * first segment matches a derived iterator name inside an enclosing array function.
 *
 * @param {{ text: string, fieldPath: string, schemaParams: Object }} params
 * @returns {Object|string|null}
 */
const resolveFieldPathInContext = ({ text, fieldPath, schemaParams }) => {
  const direct = resolveFieldPath(fieldPath, schemaParams);

  if (!_.isNil(direct)) {
    return direct;
  }

  const segments = fieldPath.split('.');
  const callInfo = findIteratorBindingCall(text, segments[0]);

  if (_.isNil(callInfo)) {
    return null;
  }

  const firstArgText = (callInfo.firstArgText ?? '').trim();
  const fn = schemaParams?.functions?.[callInfo.funcName];
  const isArrayFunc =
    (!_.isNil(fn) &&
      (ARRAY_FUNCTIONS_WITH_ITERATORS.includes(callInfo.funcName) ||
        Object.values(fn.arguments ?? {}).some(a => a.type === 'iterator'))) ||
    false;

  if (!isArrayFunc) {
    return null;
  }

  const elementEntity = resolveArrayElementEntity(firstArgText, schemaParams);

  if (!_.isPlainObject(elementEntity)) {
    return null;
  }

  const remainingSegments = segments.slice(1);

  if (!remainingSegments.length) {
    return elementEntity;
  }

  return walkPathFromEntity(remainingSegments, elementEntity, schemaParams.entities, new Set());
};

/**
 * Attempt VALUE context detection when trailing token is a comparison operator.
 *
 * @param {{ base: string, op: string, text: string, partialLen: number, schemaParams: Object }} params
 * @returns {{ context, replaceLength, suggestions }|null}
 */
const tryValueContext = ({ base, op, text, partialLen, schemaParams }) => {
  if (!COMPARISON_OPS.has(op)) {
    return null;
  }

  const opMatch = base.trimEnd().match(FULL_OP_SUFFIX_RE);

  if (!opMatch) {
    return null;
  }

  const beforeOp = base.slice(0, base.trimEnd().length - opMatch[0].length).trimEnd();
  const fieldMatch = beforeOp.match(FIELD_SUFFIX_RE);

  if (!fieldMatch) {
    return null;
  }

  const resolved = resolveFieldPathInContext({ text, fieldPath: fieldMatch[1], schemaParams });

  if (!_.isString(resolved)) {
    return null;
  }

  const enumValues = schemaParams.enumTypes?.[resolved];

  if (enumValues) {
    return {
      context: SUGGESTION_CONTEXTS.VALUE,
      replaceLength: partialLen,
      suggestions: buildEnumValueSuggestions(enumValues, text),
    };
  }

  if (resolved === 'bool' || resolved === LEX_TOKEN_TYPES.bool) {
    return {
      context: SUGGESTION_CONTEXTS.VALUE,
      replaceLength: partialLen,
      suggestions: buildBoolValueSuggestions(),
    };
  }

  return null;
};

/**
 * Attempt VALUE context detection from a function argument with lookups.
 *
 * @param {string} text
 * @param {number} partialLen
 * @param {Object} schemaParams
 * @returns {{ context, replaceLength, suggestions }|null}
 */
const tryLookupValueContext = (text, partialLen, schemaParams) => {
  const callInfo = scanForUnclosedCall(text);

  if (_.isNil(callInfo)) {
    return null;
  }

  const fn = schemaParams?.functions?.[callInfo.funcName];

  if (_.isNil(fn)) {
    return null;
  }

  const argKey = String(callInfo.activeParameter + 1);
  const argDescriptor = fn.arguments?.[argKey];

  if (!argDescriptor?.lookups) {
    return null;
  }

  const lookupValues = schemaParams?.lookups?.[argDescriptor.lookups];

  if (!lookupValues) {
    return null;
  }

  return {
    context: SUGGESTION_CONTEXTS.VALUE,
    replaceLength: partialLen,
    suggestions: buildLookupSuggestions(lookupValues, text),
  };
};

/**
 * Attempt OPERATOR context detection.
 *
 * @param {{ text: string, base: string, partial: string, trailing: string, schemaParams: Object, isOnlyBooleanAllowed: boolean }} params
 * @returns {{ context, replaceLength, suggestions }|null}
 */
const tryOperatorContext = ({
  text,
  base,
  partial,
  trailing,
  schemaParams,
  isOnlyBooleanAllowed,
}) => {
  const isLeafType = resolved =>
    _.isString(resolved) &&
    !_.isPlainObject(resolveType(resolved, schemaParams.entities, new Set()));

  // Field followed by whitespace — operator has not been typed yet.
  const fieldWithSpaceMatch = base.match(/([\w.]+)[\s]+$/);

  if (fieldWithSpaceMatch) {
    const resolved = resolveFieldPathInContext({
      text,
      fieldPath: fieldWithSpaceMatch[1],
      schemaParams,
    });

    if (isLeafType(resolved)) {
      return {
        context: SUGGESTION_CONTEXTS.OPERATOR,
        replaceLength: partial.length,
        suggestions: buildOperatorSuggestions(resolved, schemaParams, isOnlyBooleanAllowed),
      };
    }
  }

  // Trailing is 'field:path' with no partial — cursor is at the end of a leaf field.
  if (trailing.startsWith('field:') && !partial) {
    const resolved = resolveFieldPathInContext({
      text,
      fieldPath: trailing.slice('field:'.length),
      schemaParams,
    });

    if (isLeafType(resolved)) {
      return {
        context: SUGGESTION_CONTEXTS.OPERATOR,
        replaceLength: 0,
        suggestions: buildOperatorSuggestions(resolved, schemaParams, isOnlyBooleanAllowed),
      };
    }
  }

  // Trailing is a symbol operator with no partial. Only '>' and '<' are
  // extendable (to '>=' / '<='), so they keep offering operators. Every other
  // trailing operator is already complete (>=, <=, ==, !=, +, -, *, /) — the
  // operator is done, so fall through to ROOT and suggest the next operand
  // instead of re-suggesting the operator the user just typed.
  if (
    trailing.startsWith('operator:') &&
    !partial &&
    EXTENDABLE_OPERATORS.has(trailing.slice('operator:'.length))
  ) {
    const opToken = trailing.slice('operator:'.length);
    const opLen = opToken.length;
    const withoutOp = base.slice(0, base.length - opLen).trimEnd();
    const fieldBeforeOpMatch = withoutOp.match(/([\w.]+)$/);

    if (fieldBeforeOpMatch) {
      const resolved = resolveFieldPathInContext({
        text,
        fieldPath: fieldBeforeOpMatch[1],
        schemaParams,
      });

      if (isLeafType(resolved)) {
        return {
          context: SUGGESTION_CONTEXTS.OPERATOR,
          replaceLength: opLen,
          suggestions: buildOperatorSuggestions(resolved, schemaParams, isOnlyBooleanAllowed),
        };
      }
    }
  }

  // Partial is an operator character — user is mid-operator.
  const isPartialOp =
    partial &&
    (OPERATOR_CHARS_RE.test(partial[0]) || /^(?:contains|startsWith|endsWith)/.test(partial));

  if (isPartialOp) {
    const fieldBeforeMatch = base.match(/([\w.]+)[\s]+$/);

    if (fieldBeforeMatch) {
      const resolved = resolveFieldPathInContext({
        text,
        fieldPath: fieldBeforeMatch[1],
        schemaParams,
      });

      if (isLeafType(resolved)) {
        return {
          context: SUGGESTION_CONTEXTS.OPERATOR,
          replaceLength: partial.length,
          suggestions: buildOperatorSuggestions(resolved, schemaParams, isOnlyBooleanAllowed),
        };
      }
    }
  }

  return null;
};

// Type-context filtering — narrow suggestions to the scalar category the cursor expects
// (a number after '*', a date after a date comparison, …) so non-matching scalars and
// functions don't clutter the list.
const ARITHMETIC_OP_SUFFIX_RE = /[+\-*/]\s*$/;
// Only these single-char operators can grow into a longer form ('>' → '>='); every
// other operator is complete, so a trailing one means advance to the next operand.
const EXTENDABLE_OPERATORS = new Set(['>', '<']);
const COMPARISON_OP_SUFFIX_RE = /(>=|<=|==|!=|[><])\s*$/;

// Substring markers (case-insensitive) that identify each filterable category in a type
// token or pipe-delimited union (e.g. 'int|long|date').
const CATEGORY_MARKERS = {
  numeric: [...NUMERIC_TYPES, 'number', 'integer'],
  date: ['date'],
  string: ['string', 'text', 'guid'],
  array: ['array'],
};
const KNOWN_TYPE_MARKERS = [
  ...CATEGORY_MARKERS.numeric,
  ...CATEGORY_MARKERS.date,
  ...CATEGORY_MARKERS.string,
  'bool',
  'enum',
  'array',
  'entity',
  'iterator',
];

const typeParts = type =>
  String(type ?? '')
    .toLowerCase()
    .split('|')
    .map(part => part.trim())
    .filter(Boolean);

const partsMatchCategory = (parts, category) =>
  parts.some(part => (CATEGORY_MARKERS[category] ?? []).some(marker => part.includes(marker)));

// True when a (possibly union) type could satisfy the category: any part matches the
// category, OR it is generic ('any'), OR it is a fully unknown token (keep rather than
// hide a field whose type we don't recognise). Polymorphic unions like SWITCH's
// 'string|bool|…|int|…|date' match numeric AND date AND string — kept in each context.
const couldBeCategory = (type, category) => {
  const parts = typeParts(type);

  if (!parts.length || parts.includes('any') || partsMatchCategory(parts, category)) {
    return true;
  }

  return parts.every(part => !KNOWN_TYPE_MARKERS.some(marker => part.includes(marker)));
};

// The filterable category of a single field/type token (for a comparison's LHS or a
// function arg). Returns null for bool/enum/entity/unknown — those aren't narrowed
// (bool/enum comparison values are handled separately by the VALUE context) — and for a
// union spanning several categories (EMPTY/IS_SET take 'string|…|int|…|date|…|entity'),
// which accepts anything and so must not hide e.g. an entity's non-numeric fields.
const categoryOfType = type => {
  const parts = typeParts(type);
  const categories = ['numeric', 'date', 'string', 'array'].filter(category =>
    partsMatchCategory(parts, category),
  );

  return categories.length === 1 ? categories[0] : null;
};

// A human-friendly label for a (possibly pipe-union) type, for signature help —
// 'int|long|double|decimal|float' reads as 'number', not the raw union.
const FRIENDLY_CATEGORY_LABEL = { numeric: 'number', date: 'date', string: 'text', array: 'list' };

const friendlyTypeLabel = type => {
  if (_.isNil(type) || type === 'any') {
    return 'any';
  }

  const category = categoryOfType(type);

  if (category) {
    return FRIENDLY_CATEGORY_LABEL[category];
  }

  const parts = typeParts(type);

  if (parts.length > 1) {
    return 'value';
  }

  return parts[0] === 'bool' ? 'true/false' : type;
};

const isArithmeticOperand = textBeforeOperand =>
  ARITHMETIC_OP_SUFFIX_RE.test(textBeforeOperand ?? '');

// The declared type of the function argument the cursor is in (e.g. PERCENT(‹here›) →
// 'int|long|…'), or null. An array function's first arg is the collection ('array'); its
// later args are the iterator/predicate, which aren't plain typed slots, so they're skipped.
const expectedArgType = (text, schemaParams) => {
  const callInfo = scanForUnclosedCall(text ?? '');

  if (!callInfo) {
    return null;
  }

  if (ARRAY_FUNCTIONS_WITH_ITERATORS.includes(callInfo.funcName)) {
    return callInfo.activeParameter === 0 ? 'array' : null;
  }

  const args = schemaParams.functions?.[callInfo.funcName]?.arguments;

  if (_.isNil(args)) {
    return null;
  }

  const argKeys = Object.keys(args);
  const isVariadic = argKeys.some(key => !/^\d+$/.test(key));
  const descriptor = isVariadic ? args[argKeys[0]] : args[String(callInfo.activeParameter + 1)];

  return descriptor?.type ?? null;
};

// The resolved type of the field on the left of a trailing comparison operator
// ('loan.amount >= ‹here›' → 'decimal'), or null.
const comparisonLhsType = (text, base, schemaParams) => {
  const trimmed = base.trimEnd();
  const opMatch = trimmed.match(COMPARISON_OP_SUFFIX_RE);

  if (!opMatch) {
    return null;
  }

  const beforeOp = trimmed.slice(0, trimmed.length - opMatch[0].length).trimEnd();
  const fieldMatch = beforeOp.match(/([\w.]+)$/);

  if (!fieldMatch) {
    return null;
  }

  const resolved = resolveFieldPathInContext({ text, fieldPath: fieldMatch[1], schemaParams });

  return _.isString(resolved) ? resolved : null;
};

// The scalar category the cursor expects (or null): an arithmetic operand wants a
// number; a function arg / comparison RHS wants the declared / left-field category.
// `base` is the text with the trailing partial stripped.
const expectedCategory = (text, base, schemaParams) => {
  if (isArithmeticOperand(base)) {
    return 'numeric';
  }

  return (
    categoryOfType(expectedArgType(text, schemaParams)) ??
    categoryOfType(comparisonLhsType(text, base, schemaParams))
  );
};

// Keep a suggestion compatible with the expected category: drillable entities (their
// children may match), functions whose return type could be the category, and matching
// scalar fields. Array fields are dropped outside an 'array' context (they're consumed
// via aggregate functions, not directly).
const keepForCategory = (suggestion, category) => {
  if (suggestion.isEntity) {
    return true;
  }

  if (suggestion.key === 'Functions') {
    return couldBeCategory((suggestion.name?.split('→').pop() ?? '').trim(), category);
  }

  const typeSuffix = (suggestion.name?.split('·').pop() ?? '').trim();

  if (category !== 'array' && typeSuffix.startsWith('array')) {
    return false;
  }

  return couldBeCategory(typeSuffix, category);
};

const filterToCategory = (suggestions, category) =>
  category ? suggestions.filter(suggestion => keepForCategory(suggestion, category)) : suggestions;

// The name of the function whose call closed at a trailing ')', or null. The grammar
// only uses parens for function calls, so a trailing ')' always ends one — after it the
// cursor sits on a value, ready for an operator.
const closedCallFunction = base => {
  const trimmed = base.trimEnd();

  if (!trimmed.endsWith(')')) {
    return null;
  }

  const { openIndex } = [...trimmed].reduceRight(
    (acc, ch, index) => {
      if (acc.done) {
        return acc;
      }

      if (ch === ')') {
        return { ...acc, depth: acc.depth + 1 };
      }

      if (ch === '(') {
        const depth = acc.depth - 1;

        return depth === 0 ? { depth, openIndex: index, done: true } : { ...acc, depth };
      }

      return acc;
    },
    { depth: 0, openIndex: -1, done: false },
  );

  if (openIndex < 0) {
    return null;
  }

  return trimmed.slice(0, openIndex).match(/([A-Za-z_][A-Za-z0-9_]*)\s*$/)?.[1] ?? null;
};

// Exported functions

/**
 * Get proactive autocomplete suggestions based on cursor-position context.
 *
 * Returns null when the engine has no opinion and the caller should fall back
 * to the existing parser-driven flow.
 *
 * @param {{ textBeforeCursor: string, schemaParams: Object, isOnlyBooleanAllowed: boolean }} params
 * @returns {{ context: string, replaceLength: number, suggestions: Array }|null}
 */
export const getProactiveSuggestions = ({
  textBeforeCursor,
  schemaParams,
  isOnlyBooleanAllowed = false,
}) => {
  if (_.isNil(schemaParams?.entities)) {
    return null;
  }

  const rawText = textBeforeCursor ?? '';

  // When a single quote is open at the cursor (odd quote count), strip it so
  // the tokenization below can still classify the operator/field context.
  // The VALUE builder will see odd parity and emit `Value'` insertText.
  const isInsideOpenQuote = rawText.endsWith("'") && countSingleQuotes(rawText) % 2 !== 0;
  const text = isInsideOpenQuote ? rawText.slice(0, -1) : rawText;

  // PATH_DRILL_DOWN: text ends with a proper dotted path like 'loan.' or 'loan.bo'.
  // The regex requires the path before the last dot to be a valid dotted identifier
  // (no spaces/operators in the path), preventing false matches on 'loan.amount >'.
  const dottedMatch = text.match(/([\w]+(?:\.[\w]+)*\.)([\w]*)$/);

  if (dottedMatch) {
    const pathBeforeDot = dottedMatch[1].slice(0, -1);
    const partial = dottedMatch[2];
    const resolved = resolveFieldPath(pathBeforeDot, schemaParams);

    if (_.isPlainObject(resolved)) {
      const category = expectedCategory(text, text.slice(0, dottedMatch.index), schemaParams);

      return {
        context: SUGGESTION_CONTEXTS.PATH_DRILL_DOWN,
        replaceLength: partial.length,
        suggestions: filterToCategory(
          buildChildSuggestions(resolved, schemaParams, isOnlyBooleanAllowed),
          category,
        ),
      };
    }

    // Iterator drill-down: pathBeforeDot starts with a derived iterator name from
    // the innermost enclosing array function. Resolve the array element entity instead.
    return tryIteratorDrillDown({
      text,
      pathBeforeDot,
      partialLen: partial.length,
      schemaParams,
      isOnlyBooleanAllowed,
    });
  }

  const { partial, base } = stripTrailingPartial(text);
  const trailing = classifyTrailing(base);

  // VALUE context: comparison operator precedes the cursor
  if (trailing.startsWith('operator:')) {
    const op = trailing.slice('operator:'.length);
    const valueResult = tryValueContext({
      base,
      op,
      text: rawText,
      partialLen: partial.length,
      schemaParams,
    });

    if (valueResult) {
      return valueResult;
    }
  }

  // VALUE context: function argument with lookups
  const lookupResult = tryLookupValueContext(rawText, partial.length, schemaParams);

  if (lookupResult) {
    return lookupResult;
  }

  // ITERATOR context: cursor is at the iterator-name slot of an array function
  const iteratorResult = tryIteratorContext(text, partial, schemaParams);

  if (iteratorResult) {
    return iteratorResult;
  }

  // OPERATOR context
  const operatorResult = tryOperatorContext({
    text,
    base,
    partial,
    trailing,
    schemaParams,
    isOnlyBooleanAllowed,
  });

  if (operatorResult) {
    return operatorResult;
  }

  // ROOT context
  const isRootContext =
    !text.trim() ||
    trailing === 'openParen' ||
    trailing === 'comma' ||
    trailing === 'empty' ||
    trailing.startsWith('operator:');

  if (isRootContext) {
    return {
      context: SUGGESTION_CONTEXTS.ROOT,
      replaceLength: partial.length,
      suggestions: filterToCategory(
        buildRootSuggestions(schemaParams, isOnlyBooleanAllowed),
        expectedCategory(text, base, schemaParams),
      ),
    };
  }

  // CLOSED-CALL context: cursor right after a ')' that closed a function call. The
  // result is a value, so offer operators for its return type rather than letting the
  // caller fall back to the (noisy) full schema list.
  const closedFn = closedCallFunction(base);

  if (closedFn && !partial) {
    const returnType = schemaParams.functions?.[closedFn]?.returnType;

    if (!_.isNil(returnType)) {
      return {
        context: SUGGESTION_CONTEXTS.OPERATOR,
        replaceLength: 0,
        suggestions: buildOperatorSuggestions(returnType, schemaParams, isOnlyBooleanAllowed),
      };
    }
  }

  return null;
};

/**
 * Get function signature help based on cursor position.
 *
 * @param {{ textBeforeCursor: string, schemaParams: Object }} params
 * @returns {{ functionName: string, label: string, parameters: Array, activeParameter: number, documentation: string }|null}
 */
export const getSignatureHelp = ({ textBeforeCursor, schemaParams }) => {
  if (_.isNil(schemaParams?.functions)) {
    return null;
  }

  const text = textBeforeCursor ?? '';
  const callInfo = scanForUnclosedCall(text);

  if (_.isNil(callInfo)) {
    return null;
  }

  const { funcName, activeParameter } = callInfo;
  const fn = schemaParams.functions[funcName];

  if (_.isNil(fn)) {
    return null;
  }

  const isArrayFunc = ARRAY_FUNCTIONS_WITH_ITERATORS.includes(funcName);
  const args = fn.arguments ?? {};
  const argKeys = Object.keys(args);
  const allNumeric = argKeys.every(k => /^\d+$/.test(k));

  const buildPositionalParameters = () => {
    if (isArrayFunc) {
      // Exclude iterator-typed arguments — they are synthetic (auto-derived from
      // the array arg) and must not appear as explicit typed parameters in the
      // signature. This handles both the 2-arg fixture shape
      // {1:array, 2:bool} and the real 3-arg-with-iterator schema shape
      // {1:array, 2:iterator, 3:bool}, producing the same 2-param signature.
      const nonIteratorEntries = Array.from(
        { length: fn.argumentsCount },
        (_, i) => args[String(i + 1)],
      ).filter(descriptor => descriptor?.type !== 'iterator');

      return nonIteratorEntries.map((_, idx) => {
        if (idx === 0) {
          return { label: 'array', documentation: 'array' };
        }

        return {
          label: 'predicate: bool',
          documentation:
            'Reference each element inside the predicate as the derived iterator (e.g. _borrowertask for Application.BorrowerTasks)',
        };
      });
    }

    return Array.from({ length: fn.argumentsCount }, (_, i) => {
      const argDescriptor = args[String(i + 1)];
      const argType = friendlyTypeLabel(argDescriptor?.type ?? 'any');
      const isOptional = argDescriptor?.optional === true;
      const lookupNote = argDescriptor?.lookups ? ` — one of lookups.${argDescriptor.lookups}` : '';
      const optionalSuffix = isOptional ? '?' : '';
      const paramLabel = `arg${i + 1}: ${argType}${optionalSuffix}`;

      return { label: paramLabel, documentation: `${paramLabel}${lookupNote}` };
    });
  };

  const buildVariadicParameters = () => {
    const samplerKey = argKeys[0];
    const samplerType = friendlyTypeLabel(args[samplerKey]?.type ?? 'any');

    return Array.from({ length: fn.argumentsCount }, (_, i) => {
      const paramLabel = `arg${i + 1}: ${samplerType}`;

      return { label: paramLabel, documentation: paramLabel };
    });
  };

  const getParameters = () => {
    if (fn.argumentsCount === 0) {
      return [];
    }

    if (allNumeric) {
      return buildPositionalParameters();
    }

    return buildVariadicParameters();
  };

  const parameters = getParameters();

  const returnType = fn.returnType ?? '';
  const paramListStr = parameters.map(p => p.label).join(', ');
  const label = `${funcName}(${paramListStr}) → ${returnType}`;
  const documentation =
    isArrayFunc && funcName === 'ANY'
      ? 'Use ANY for list membership — contains is substring-only.'
      : `${funcName}: returns ${returnType}`;

  return {
    functionName: funcName,
    label,
    parameters,
    activeParameter,
    documentation,
  };
};
